/* Audio timing model — the heart of "recite along".

   Every reciter, whatever the source, is normalised into a Track: one audio URL
   plus an ordered list of ayah segments, each optionally carrying word
   segments. The audio engine and the mushaf only ever see this shape. */
import { RECITERS, type Clip, type Reciter } from '@/data/reciters';
import { JUZS, chapter } from '@/features/quran/api';

export interface WordSeg { word: number; start: number; end: number } // ms, word = 1-based position
export interface AyahSeg { verseKey: string; start: number; end: number; words: WordSeg[] }
export interface Track {
  reciterId: string;
  url: string;
  /** surah this track was requested for */
  surah: number;
  /** set for one-file-per-juz recordings */
  juz?: number;
  /** set for passage/clip recordings */
  clip?: Clip;
  ayahs: AyahSeg[];
  /** 'none' → continuous playback only (alignment not generated yet) */
  granularity: 'word' | 'ayah' | 'none';
}

interface QfTimestamp { verse_key: string; timestamp_from: number; timestamp_to: number; segments?: number[][] }

/* Quran.com segments are [wordIndex, startMs, endMs]; malformed entries
   (single-element arrays appear in the real data) are dropped. */
export function normaliseQf(ts: QfTimestamp[]): AyahSeg[] {
  return ts.map((t) => ({
    verseKey: t.verse_key,
    start: t.timestamp_from,
    end: t.timestamp_to,
    words: (t.segments ?? [])
      .filter((s) => s.length >= 3 && Number.isFinite(s[1]) && Number.isFinite(s[2]))
      .map(([w, a, b]) => ({ word: w, start: a, end: b })),
  }));
}

const pad3 = (n: number) => String(n).padStart(3, '0');

export function juzForSurah(surah: number): number {
  return JUZS.find((j) => j.map[String(surah)])?.n ?? 1;
}

/** Aligned timings produced by scripts/align, published under /timings/. */
async function alignedTimings(reciterId: string, file: string): Promise<AyahSeg[] | null> {
  try {
    const r = await fetch(`/timings/${reciterId}/${file}.json`);
    if (!r.ok) return null;
    return (await r.json()) as AyahSeg[];
  } catch { return null; }
}

export async function loadTrack(reciter: Reciter, surah: number, juzHint?: number, clipId?: string): Promise<Track> {
  if (reciter.audio === 'clips') {
    const clip = reciter.clips.find((c) => c.id === clipId) ?? reciter.clips.find((c) => c.surah === surah) ?? reciter.clips[0];
    const ayahs = (await alignedTimings(reciter.id, clip.id)) ?? [];
    return { reciterId: reciter.id, url: clip.url, surah: clip.surah, juz: clip.juz, clip, ayahs, granularity: ayahs.length ? (ayahs.some((a) => a.words.length) ? 'word' : 'ayah') : 'none' };
  }
  if (reciter.audio === 'qf') {
    const r = await fetch(`/api/qf/chapter_recitations/${reciter.qfId}/${surah}?segments=true`);
    if (!r.ok) throw new Error(`Audio ${r.status}`);
    const j = (await r.json()) as { audio_file: { audio_url: string; timestamps?: QfTimestamp[] } };
    const ayahs = normaliseQf(j.audio_file.timestamps ?? []);
    const hasWords = ayahs.some((a) => a.words.length > 0);
    return { reciterId: reciter.id, url: j.audio_file.audio_url, surah, ayahs, granularity: ayahs.length ? (hasWords ? 'word' : 'ayah') : 'none' };
  }
  if (reciter.audio === 'template') {
    const url = reciter.url.replace('{sss}', pad3(surah));
    const ayahs = (await alignedTimings(reciter.id, pad3(surah))) ?? [];
    return { reciterId: reciter.id, url, surah, ayahs, granularity: ayahs.length ? (ayahs.some((a) => a.words.length) ? 'word' : 'ayah') : 'none' };
  }
  // juz files: a surah can span juz boundaries — start from the juz holding its first ayah.
  const juz = juzHint ?? juzForSurah(surah);
  const url = reciter.base + encodeURIComponent(reciter.juzFiles[juz - 1]);
  const all = (await alignedTimings(reciter.id, `juz-${String(juz).padStart(2, '0')}`)) ?? [];
  return { reciterId: reciter.id, url, surah, juz, ayahs: all, granularity: all.length ? (all.some((a) => a.words.length) ? 'word' : 'ayah') : 'none' };
}

/** Binary search: which ayah/word is playing at `ms`. */
export function locate(track: Track, ms: number): { ayahIdx: number; word: number | null } {
  const a = track.ayahs;
  if (!a.length) return { ayahIdx: -1, word: null };
  let lo = 0, hi = a.length - 1, idx = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (a[mid].start <= ms) { idx = mid; lo = mid + 1; } else hi = mid - 1;
  }
  if (idx < 0) return { ayahIdx: 0, word: null };
  const words = a[idx].words;
  let word: number | null = null;
  for (let i = words.length - 1; i >= 0; i--) {
    if (words[i].start <= ms) { word = ms <= words[i].end + 120 ? words[i].word : null; break; }
  }
  return { ayahIdx: idx, word };
}

export const ayahIndex = (track: Track, verseKey: string) => track.ayahs.findIndex((a) => a.verseKey === verseKey);

export const firstSurahOfJuz = (juz: number) => Number(JUZS[juz - 1].first.split(':')[0]);

export const reciterAvailableFor = (surah: number) => RECITERS.filter((r) => !r.surahs || r.surahs.includes(surah));

export const surahTitle = (s: number) => `${chapter(s).n} · ${chapter(s).ar}`;
